import { SURVEY_STATUSES } from "@/modules/session-survey/consts/surveyStatuses";
import { SurveyDetails } from "@/modules/session-survey/types/SurveyDetails";
import { SurveyQuestion } from "@/modules/session-survey/types/SurveyQuestion";
import { apiClient } from "@/modules/shared/utils/apiClient";

interface WireSurveyQuestion {
  id?: unknown;
  order?: unknown;
  type?: unknown;
  prompt?: unknown;
  required?: unknown;
  clientMetadata?: {
    body?: unknown;
    minLabel?: unknown;
    maxLabel?: unknown;
    shouldCenterTitle?: unknown;
    isContinueWithCoach?: unknown;
  } | null;
  options?: unknown;
}

interface WireSurveyDetails extends Omit<SurveyDetails, "definition"> {
  definition?: {
    version?: string;
    questions?: WireSurveyQuestion[] | null;
  };
}

type SurveyQuestionOption = NonNullable<SurveyQuestion["options"]>[number];

const normalizeOption = (option: unknown): SurveyQuestionOption | undefined => {
  if (typeof option !== "object" || option === null) {
    if (!["string", "number", "boolean"].includes(typeof option)) {
      return undefined;
    }

    const value = String(option);

    if (value.trim().length === 0) return undefined;

    return { value, label: value };
  }

  const wireOption = option as Record<string, unknown>;
  const rawValue = wireOption.value ?? wireOption.id;

  if (
    !["string", "number", "boolean"].includes(typeof rawValue) ||
    String(rawValue).trim().length === 0
  ) {
    return undefined;
  }

  const value = String(rawValue);
  const rawLabel = wireOption.label;
  const label =
    typeof rawLabel === "string" && rawLabel.trim().length > 0
      ? rawLabel
      : value;

  return { value, label };
};

const normalizeOptions = (
  options: unknown,
): SurveyQuestion["options"] | undefined => {
  if (options === undefined || options === null) return undefined;

  if (!Array.isArray(options)) return undefined;

  const values = new Set<string>();

  return options.flatMap((option) => {
    const normalizedOption = normalizeOption(option);

    if (!normalizedOption || values.has(normalizedOption.value)) return [];

    values.add(normalizedOption.value);

    return [normalizedOption];
  });
};

const normalizeClientMetadata = (
  question: WireSurveyQuestion,
): SurveyQuestion["clientMetadata"] => {
  const body = question.clientMetadata?.body;
  const minLabel = question.clientMetadata?.minLabel;
  const maxLabel = question.clientMetadata?.maxLabel;
  const shouldCenterTitle = question.clientMetadata?.shouldCenterTitle;
  const isContinueWithCoach = question.clientMetadata?.isContinueWithCoach;
  const normalizedMetadata = {
    ...(typeof body === "string" ? { body } : {}),
    ...(typeof minLabel === "string" ? { minLabel } : {}),
    ...(typeof maxLabel === "string" ? { maxLabel } : {}),
    ...(shouldCenterTitle === true || shouldCenterTitle === "1"
      ? { shouldCenterTitle: true }
      : {}),
    ...(isContinueWithCoach === true ||
    isContinueWithCoach === 1 ||
    isContinueWithCoach === "1"
      ? { isContinueWithCoach: true }
      : {}),
  };

  return Object.keys(normalizedMetadata).length > 0
    ? normalizedMetadata
    : undefined;
};

const mapSurveyQuestion = (
  question: WireSurveyQuestion,
  fallbackOrder: number,
): SurveyQuestion | undefined => {
  if (
    !["string", "number"].includes(typeof question.id) ||
    String(question.id).trim().length === 0
  ) {
    return undefined;
  }

  return {
    id: String(question.id),
    order:
      typeof question.order === "number" && Number.isFinite(question.order)
        ? question.order
        : fallbackOrder,
    type:
      typeof question.type === "string" && question.type.trim().length > 0
        ? question.type
        : "unknown",
    prompt: typeof question.prompt === "string" ? question.prompt : "",
    required: question.required === true,
    clientMetadata: normalizeClientMetadata(question),
    options: normalizeOptions(question.options),
  };
};

// Completed/expired payloads carry only the status — synthesize an empty
// definition so status screens render instead of the mapper throwing.
const mapSurveyDetails = ({
  definition,
  ...details
}: WireSurveyDetails): SurveyDetails => ({
  ...details,
  definition: definition
    ? {
        version: definition.version ?? "",
        questions: Array.isArray(definition.questions)
          ? definition.questions.flatMap((question, index) => {
              const mappedQuestion = mapSurveyQuestion(question, index);

              return mappedQuestion ? [mappedQuestion] : [];
            })
          : [],
      }
    : { version: "", questions: [] },
});

export const getSurveyDetails = async (
  surveyId: string,
): Promise<SurveyDetails> => {
  const details = await apiClient.get<WireSurveyDetails>(`/survey/${surveyId}`);

  if (
    typeof details !== "object" ||
    details === null ||
    !SURVEY_STATUSES.includes(details.status)
  ) {
    throw new Error("Invalid survey response");
  }

  return mapSurveyDetails(details);
};
